<template>
  <div>
    <div class="tw-flex tw-justify-between tw-items-center">
      <base-text-field v-model="query" ref="searchInput" class="tw-flex-grow" placeholder="Search for a plan" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <base-button class="tw-ml-5 tw-hidden sm:tw-flex" type="primary" @click="add">
        <template v-slot:icon>
          <plus-icon />
        </template>
        Add Plan
      </base-button>
      <base-icon @click="add" class="tw-ml-3 tw-inline sm:tw-hidden">
        <plus-icon color="green" class="tw-h-5 tw-w-5" />
      </base-icon>
    </div>
    <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-5">
      <template #icon="{item: plan}">
        <Avatar :height="48" :width="48" :editable="true" :businessId="businessId" :planId="plan.businessPlanId" avatarType="plan" />
      </template>
      <template #plan="{item: plan}">
        <div class="tw-flex tw-items-center">
          {{ plan.name }}
          <base-badge v-if="plan.tag" size="sm" class="tw-ml-1.5">{{ plan.tag }}</base-badge>
        </div>
        <div class="tw-text-gray-500 tw-text-xs">{{ plan.planGroup }}</div>
      </template>
      <template #price="{item: plan}">
        <div>{{ formatCyclePrice(plan) }}</div>
      </template>
      <template #products="{item: plan}">
        <div v-for="itemCategory in plan.itemCategories" :key="itemCategory.businessItemCategoryId">
          <div>
            <span v-if="itemCategory.quantity > 0" class="tw-mr-1">{{ itemCategory.quantity }}</span
            >{{ itemCategory.name }}
          </div>
        </div>
      </template>
      <template #attributes="{item: plan}">
        <div class="tw-flex tw-flex-col tw-space-y-2">
          <base-badge v-if="plan.isAutoRenew" size="xs" color="gray" class="tw-grow-0 tw-whitespace-nowrap">Auto renew</base-badge>
          <base-badge v-if="plan.isPrivate === true" size="xs" color="gray" class="tw-grow-0 tw-whitespace-nowrap">Private plan</base-badge>
        </div>
      </template>
      <template #actions="{item: plan}">
        <div class="tw-flex tw-items-center tw-space-x-2">
          <base-button type="flat" @click="term(plan)">
            <template v-slot:icon>
              <document-icon /> </template
            >Terms
          </base-button>
          <base-button v-if="organization.useTodoSetting === true" type="flat" class="tw-pr-4.5" @click="activities(plan)">
            <template v-slot:icon>
              <calendar-icon color="primary" /> </template
            >Activities<base-badge class="tw-absolute tw--right-1 tw-top-1" color="primary" :dark="true" size="xs">{{ plan.countScheduledActivities }}</base-badge>
          </base-button>
          <base-button type="flat" @click="edit(plan)">
            <template v-slot:icon>
              <edit-icon /> </template
            >Edit
          </base-button>
          <base-button type="flat" @click="del(plan)">
            <template v-slot:icon>
              <trash-icon /> </template
            >Delete
          </base-button>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-pl-4">
          <base-skeleton-loader class="tw-h-10 tw-w-10 tw-rounded-full" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-flex-col">
            <base-skeleton-loader class="tw-h-4 tw-w-36 tw-mb-2" />
            <base-skeleton-loader class="tw-h-3 tw-w-16" />
          </div>
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-4 tw-w-24 tw-mb-2" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-4 tw-w-28 tw-mb-2" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-flex-col tw-gap-y-2">
            <base-skeleton-loader class="tw-h-2 tw-w-16" />
            <base-skeleton-loader class="tw-h-2 tw-w-16" />
          </div>
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-justify-between">
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
          </div>
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <div class="tw-flex tw-flex-col">
      <business-plan-slideover :businessId="businessId" ref="planSlideover" @save="loadPage" />
      <BusinessPlanAgreementModal ref="planTermsModal" @terms="loadPage" />
      <BusinessPlanDeleteConfirmationModal ref="deleteConfirmationModal" @delete="loadPage" />
    </div>
  </div>
</template>

<script>
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, DocumentIcon, CalendarIcon } from "@/assets/svgs";
import Avatar from "@/components/Avatar";
import BusinessPlanSlideover from "@/views/business/business-plans/components/BusinessPlanSlideover.vue";
import BusinessPlanDeleteConfirmationModal from "@/views/business/business-plans/components/BusinessPlanDeleteConfirmationModal";
import BusinessPlanAgreementModal from "@/views/business/business-plans/components/BusinessPlanAgreementModal.vue";
import { debounce } from "debounce";
import { mapGetters } from "vuex";

const TAKE_KEY = "predictile_business_plans_take";

export default {
  components: {
    BusinessPlanSlideover,
    BusinessPlanDeleteConfirmationModal,
    BusinessPlanAgreementModal,
    Avatar,
    SearchIcon,
    TrashIcon,
    EditIcon,
    PlusIcon,
    DocumentIcon,
    CalendarIcon,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      fields: [
        { title: "", key: "icon", thClass: "tw-w-0" },
        { title: "Plan", key: "plan", tdClass: "tw-whitespace-nowrap" },
        { title: "Price", key: "price", thClass: "" },
        { title: "Products", key: "products", thClass: "" },
        { title: "Settings", key: "attributes", thClass: "tw-w-0" },
        { title: "Actions", key: "actions", thClass: "tw-text-center" },
      ],
      query: null,
      isLoading: false,
      planGroups: [],
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
  },
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    const searchInputRef = this.$refs.searchInput;
    searchInputRef.focus();
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  methods: {
    add() {
      this.$refs.planSlideover.show(null, this.planGroups);
    },
    edit(item) {
      this.$refs.planSlideover.show(item, this.planGroups);
    },
    activities(item) {
      this.$router.push({ name: "activity-schedule", params: { businessID: this.businessId, businessPlanId: item.businessPlanId, businessPlanName: item.name } });
    },
    del(item) {
      this.$refs.deleteConfirmationModal.show(this.businessId, item.businessPlanId, item.name);
    },
    term(item) {
      this.$refs.planTermsModal.show(
        item.name,
        item.businessPlanId,
        item.priceCents,
        item.currency,
        item.downPaymentCents,
        item.businessName,
        item.termLength,
        item.termInterval,
        item.billingFrequency,
        item.billingInterval,
        item.serviceFeeCents,
      );
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/plans`;
      url += `?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .get(url)
        .then((res) => {
          this.items = res.data.items;
          this.page.total = res.data.total;
          this.planGroups = this.getGroups(res.data.items);
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load plans`,
            message: `We had a problem retrieving the list of plans. Please reload the page to try again.`,
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getGroups(items) {
      let groups = [...new Map(items.map((item) => [item["planGroup"], { value: item["planGroup"], label: item["planGroup"] }])).values()].filter((x) => x.value !== null);
      groups.push({ value: "new group", label: "+ Add New Group" });
      return groups;
    },
    formatCyclePrice(plan) {
      if (plan.isAutoRenew === true) {
        plan.formattedTerm =
          plan.termInterval == "year"
            ? (12 * plan.termLength).toString().concat(" ", "months")
            : plan.termLength.toString().concat(" ", plan.termLength > 1 ? `${plan.termInterval}s` : plan.termInterval);
      } else {
        if (plan.cycleEndCount === null)
          plan.formattedTerm =
            plan.cycleEndIntervalCount > 1 ? plan.cycleEndIntervalCount + " " + plan.cycleEndInterval + "s" : plan.cycleEndIntervalCount + " " + plan.cycleEndInterval;
        else plan.formattedTerm = plan.cycleEndCount + " payments";
      }

      plan.formattedPrice =
        new Intl.NumberFormat(window.navigator.language, { style: "currency", currency: plan.currency }).format(plan.priceCents / 100) +
        " every " +
        plan.billingFrequency +
        " " +
        plan.billingInterval;

      if (plan.billingFrequency > 1) plan.formattedPrice += "s";
      plan.formattedPrice = plan.formattedPrice.replace("every 1", "/");

      if (plan.downPaymentCents > 0) {
        plan.formattedPrice +=
          " with " + new Intl.NumberFormat(window.navigator.language, { style: "currency", currency: plan.currency }).format(plan.downPaymentCents / 100) + " down";
      }

      return plan.formattedPrice;
    },
  },
};
</script>
